import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useDisplayName } from '@/composables/useDisplayName';
import { usePermissions } from '@/composables/usePermissions';
import { useToast } from '@/composables/useToast';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { IOnboardingStudent } from '@/interfaces';
import { assertArrayNotEmpty } from '@/utils/assertion.utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { EIcon } from 'common/enum/misc.enum';
import { useI18n } from 'vue-i18n';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { useRefreshSetupChecklist } from '../setupChecklist/useRefreshSetupChecklist';
import { supportMeasureEntryKeys } from '../supportMeasureEntry/supportMeasureEntry.keys';
import { applyCreatedStudents, ensureStudentClientKey } from '@/utils/students.utils';
import { useStudentAvatarUploads } from '../useStudentAvatarUploads';
import { useRefreshStudents } from './useRefreshStudents';

interface IStudentMutationOptions {
  refreshList?: boolean;
  showToast?: boolean;
}

/** Student actions never subscribe to student lists. */
export function useStudentMutations() {
  const { editSuccessToast, catchError, customSuccessToast } = useToast();
  const { hasTenantAdminAccess } = usePermissions();
  const { getUserDisplayName } = useDisplayName();
  const { t } = useI18n();
  const { whoAmI } = useWhoAmI();
  const { confirm } = useConfirmDialog();
  const { uploadStudentAvatarPictures } = useStudentAvatarUploads();
  const trialUpsellStore = useTrialUpsellStore();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();

  const queryClient = useQueryClient();
  const { refreshStudents: refresh } = useRefreshStudents();

  /**
   * bulk creation of students without toast, optionally assigned to classes upon creation.
   * All students are created first; avatar pictures are uploaded afterwards so that a failed
   * upload never prevents a student from being created or the remaining uploads from running.
   */
  async function createBulkStudents({
    students,
    schoolClassIds,
  }: {
    students: IOnboardingStudent[];
    schoolClassIds?: number[];
  }): Promise<Components.Schemas.StudentDto[]> {
    if (!students.length) return [];
    const pendingStudents = students.filter((student) => student.id == null);
    const createdStudents = pendingStudents.length
      ? await catchError(async () => {
          const response = await Api.Routes.Student.createBulk(null, {
            students: assertArrayNotEmpty(
              pendingStudents.map((student) => ({
                clientKey: ensureStudentClientKey(student),
                firstName: student.firstName.trim(),
                lastName: student.lastName.trim(),
                avatar: student.avatar,
                schoolClassIds,
              })),
            ),
          });
          applyCreatedStudents(pendingStudents, response.data);
          return response.data;
        })
      : [];

    await uploadStudentAvatarPictures(students);

    await refresh();
    await refreshSetupChecklist();
    trialUpsellStore.recordStudentsCreatedOrLinked(createdStudents.length);
    trialUpsellStore.requestEligibilityCheck();

    return createdStudents;
  }

  async function linkStudentsToClass({
    classId,
    students,
  }: {
    classId: number;
    students: Components.Schemas.StudentDto[];
  }): Promise<void> {
    if (!students.length) return;
    const dto: Components.Schemas.EditStudentClassRelationDto = {
      ids: assertArrayNotEmpty(
        students.map((student) => ({
          studentId: student.id,
          schoolClassId: classId,
        })),
      ),
    };
    await Api.Routes.StudentClassRelation.link(null, dto);
    await refresh();
    await refreshSetupChecklist();
    trialUpsellStore.recordStudentsCreatedOrLinked(students.length);
    trialUpsellStore.requestEligibilityCheck();
  }

  async function unlinkStudentFromClass({ classId, studentId }: { classId: number; studentId: number }): Promise<void> {
    const dto: Components.Schemas.EditStudentClassRelationDto = {
      ids: [
        {
          studentId,
          schoolClassId: classId,
        },
      ],
    };
    await catchError(async () => {
      await Api.Routes.StudentClassRelation.unlink(null, dto);
      // leaving the class detaches the student from the class' active support measures server-side
      await queryClient.invalidateQueries({ queryKey: supportMeasureEntryKeys.all });
      await refresh();
      await refreshSetupChecklist();
      customSuccessToast(t('student.removeSuccess'));
    });
  }

  async function updateStudent({
    studentId,
    data,
    options,
  }: {
    studentId: number;
    data: Components.Schemas.UpdateStudentDto;
    options?: IStudentMutationOptions;
  }): Promise<Components.Schemas.StudentDto> {
    const result = await catchError(async () => {
      const response = await Api.Routes.Student.update({ id: studentId }, data);
      // changing the class list detaches the student from the dropped classes' active support measures server-side
      if (data.schoolClassIds !== undefined) {
        await queryClient.invalidateQueries({ queryKey: supportMeasureEntryKeys.all });
      }
      if (options?.refreshList ?? true) await refresh();
      if (options?.showToast ?? true) editSuccessToast();
      return response.data;
    });

    return result;
  }

  async function removeStudent(studentId: number): Promise<void> {
    if (!hasTenantAdminAccess.value) return;

    await catchError(async () => {
      await Api.Routes.Student.delete({ id: studentId });
      await refresh();
      await refreshSetupChecklist();
      customSuccessToast(t('students.deleteSuccess'));
    });
  }

  async function removeStudentsBulk(students: Components.Schemas.StudentDto[]): Promise<boolean> {
    if (!students.length) return false;
    if (!hasTenantAdminAccess.value) return false;

    const confirmInputCheckValues = [getUserDisplayName(whoAmI.value ?? {}), whoAmI.value?.email].filter(
      (value): value is string => !!value,
    );
    const isMultiDelete = students.length > 1;

    const confirmed = await confirm({
      title: t(isMultiDelete ? 'students.deleteBulkTitle' : 'students.deleteConfirmTitle'),
      description: t('students.deleteBulkConfirmDescription', { count: students.length }),
      confirmButtonLabel: t('students.deleteBulk'),
      declineButtonLabel: t('general.cancel'),
      theme: 'danger',
      size: 'lg',
      confirmButtonIcon: EIcon.DELETE,
      hint: t('students.deleteBulkConfirmHint'),
      checkInput: {
        inputPlaceholder: t('students.deleteBulkConfirmInputPlaceholder'),
        inputCheckValue: confirmInputCheckValues,
      },
    });

    if (!confirmed) return false;

    let bulkDeleteSuccess = false;

    try {
      await catchError(async () => {
        for (const student of students) {
          await Api.Routes.Student.delete({ id: student.id });
        }
        await refresh();
        await refreshSetupChecklist();
        bulkDeleteSuccess = true;
        customSuccessToast(t('students.deleteBulkSuccess', { count: students.length }));
      });
    } catch {
      return false;
    }

    return bulkDeleteSuccess;
  }

  const createBulk = useMutation({ mutationFn: createBulkStudents });
  const link = useMutation({ mutationFn: linkStudentsToClass });
  const unlink = useMutation({ mutationFn: unlinkStudentFromClass });
  const update = useMutation({ mutationFn: updateStudent });
  const remove = useMutation({ mutationFn: removeStudent });
  const removeBulk = useMutation({ mutationFn: removeStudentsBulk });

  return {
    createBulkStudents: createBulk.mutateAsync,
    linkStudentsToClass: link.mutateAsync,
    unlinkStudentFromClass: unlink.mutateAsync,
    updateStudent: update.mutateAsync,
    removeStudent: remove.mutateAsync,
    removeStudentsBulk: removeBulk.mutateAsync,
  };
}
